import type { TovMapAttemptSummaryResponse } from "contracts/api/client-tov/mapping-attempts";
import { TOV_MAP_ATTEMPT_STATUS } from "contracts/enums/tov-mapping";
import type { TovMappingState } from "../../enums/tov";
import { TOV_MAPPING_STATE } from "../../enums/tov";

// The one frontend interpretation of a version's latest mapping attempt.
// Source status stays responsible for upload/parse/vectorize state; whether a
// MAPPING run is queued, running, failed or recoverable is answered here, from
// the attempt log, never inferred from sources flipping in and out of
// `embedded`/`failed`.
export type MappingState = TovMappingState;

export function mappingState(
  summary: TovMapAttemptSummaryResponse | null | undefined,
): MappingState {
  if (!summary) return TOV_MAPPING_STATE.UNKNOWN;
  const attempt = summary.attempt;
  if (!attempt) return TOV_MAPPING_STATE.NONE;
  if (attempt.status === TOV_MAP_ATTEMPT_STATUS.FAILED) {
    // A snapshot (recorded on first worker contact) is what the backend needs
    // to resume the same attempt from its checkpoints; without one the map
    // endpoint starts a fresh attempt instead.
    return attempt.sourceIds !== null
      ? TOV_MAPPING_STATE.FAILED_RESUMABLE
      : TOV_MAPPING_STATE.FAILED_RESTART;
  }
  return attempt.status;
}

export function isMappingFailed(state: MappingState): boolean {
  return (
    state === TOV_MAPPING_STATE.FAILED_RESUMABLE ||
    state === TOV_MAPPING_STATE.FAILED_RESTART
  );
}

/** Label for the map action: resume reuses checkpoints, anything else drafts fresh. */
export function mapActionLabel(state: MappingState): string {
  return state === TOV_MAPPING_STATE.FAILED_RESUMABLE
    ? "Resume mapping"
    : "Draft from evidence";
}

/**
 * "466 of 468 page groups retained; 2 need retry" — derived from the per-group
 * checkpoint aggregates. Null while nothing has been checkpointed.
 */
export function checkpointProgressLabel(
  summary: TovMapAttemptSummaryResponse | null | undefined,
): string | null {
  if (!summary || summary.groups.length === 0) return null;
  const total = summary.groups.reduce(
    (count, group) => count + group.checkpointed,
    0,
  );
  const failed = summary.groups.reduce(
    (count, group) => count + group.failed,
    0,
  );
  if (total === 0) return null;
  const retained = total - failed;
  const unit = total === 1 ? "page group" : "page groups";
  return failed > 0
    ? `${retained} of ${total} ${unit} retained; ${failed} need retry`
    : `${total} ${unit} checkpointed`;
}
